/**
 * Translation Error Handler
 * 
 * Provides centralized error handling for translation failures.
 * Implements graceful fallback to English for all translation errors.
 * Logs translation errors for monitoring and debugging.
 */

import TranslationService, { type SupportedLanguage } from '../services/translationService';

export interface TranslationError {
  key?: string;
  language?: SupportedLanguage;
  errorType: TranslationErrorType;
  message: string;
  timestamp: Date;
  originalError?: Error;
}

export enum TranslationErrorType {
  MISSING_TRANSLATION = 'MISSING_TRANSLATION',
  MISSING_TRANSLATION_FILE = 'MISSING_TRANSLATION_FILE',
  INVALID_KEY = 'INVALID_KEY',
  INVALID_LANGUAGE = 'INVALID_LANGUAGE',
  AI_TRANSLATION_FAILED = 'AI_TRANSLATION_FAILED',
  NETWORK_ERROR = 'NETWORK_ERROR',
  CACHE_ERROR = 'CACHE_ERROR',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR'
}

export class TranslationErrorHandler {
  // Store recent errors for debugging (max 100 errors)
  private static errorLog: TranslationError[] = [];
  private static readonly MAX_ERROR_LOG_SIZE = 100;

  // Error listeners for monitoring
  private static errorListeners: Array<(error: TranslationError) => void> = [];

  /**
   * Handle missing translation key
   * Falls back to English translation or returns the key itself
   */
  static handleMissingTranslation(
    key: string,
    language: SupportedLanguage,
    params?: Record<string, any>
  ): string {
    const error: TranslationError = {
      key,
      language,
      errorType: TranslationErrorType.MISSING_TRANSLATION,
      message: `Translation not found for key: ${key} in language: ${language}`,
      timestamp: new Date()
    };

    this.logError(error);

    // Try to get English fallback
    if (language !== 'en') {
      try {
        const englishTranslation = TranslationService.translate(key, 'en', params);
        if (englishTranslation !== key) {
          console.warn(`Using English fallback for missing ${language} translation: ${key}`);
          return englishTranslation;
        }
      } catch (fallbackError) {
        console.error('English fallback also failed:', fallbackError);
      }
    }

    // Return the key itself as last resort
    console.warn(`No translation available for key: ${key}, returning key`);
    return key;
  }

  /**
   * Handle missing translation file
   * Falls back to English translations
   */
  static handleMissingTranslationFile(
    language: SupportedLanguage,
    originalError?: Error
  ): void {
    const error: TranslationError = {
      language,
      errorType: TranslationErrorType.MISSING_TRANSLATION_FILE,
      message: `Translation file not found for language: ${language}`,
      timestamp: new Date(),
      originalError
    };

    this.logError(error);

    if (language !== 'en') {
      console.warn(`Translation file missing for ${language}, falling back to English`);
    } else {
      console.error('English translation file missing! Application may not function correctly.');
    }
  }

  /**
   * Handle invalid translation key
   * Returns the key itself
   */
  static handleInvalidKey(key: string, language: SupportedLanguage): string {
    const error: TranslationError = {
      key,
      language,
      errorType: TranslationErrorType.INVALID_KEY,
      message: `Invalid translation key format: ${key}`,
      timestamp: new Date()
    };

    this.logError(error);
    console.warn(`Invalid translation key: ${key}`);
    return key;
  }

  /**
   * Handle invalid language code
   * Falls back to English
   */
  static handleInvalidLanguage(language: string): SupportedLanguage {
    const error: TranslationError = {
      errorType: TranslationErrorType.INVALID_LANGUAGE,
      message: `Invalid language code: ${language}`,
      timestamp: new Date()
    };

    this.logError(error);
    console.warn(`Invalid language code: ${language}, falling back to English`);
    return 'en';
  }

  /**
   * Handle AI translation failure
   * Returns original text with error indication
   */
  static handleAITranslationError(
    originalText: string,
    fromLang: SupportedLanguage,
    toLang: SupportedLanguage,
    originalError: Error
  ): string {
    const error: TranslationError = {
      errorType: TranslationErrorType.AI_TRANSLATION_FAILED,
      message: `AI translation failed from ${fromLang} to ${toLang}: ${originalError.message}`,
      timestamp: new Date(),
      originalError
    };

    this.logError(error);
    console.error('AI translation failed:', originalError);
    
    // Return original text (caller should handle display)
    return originalText;
  }

  /**
   * Handle network error during translation
   */
  static handleNetworkError(originalError: Error): void {
    const error: TranslationError = {
      errorType: TranslationErrorType.NETWORK_ERROR,
      message: `Network error during translation: ${originalError.message}`,
      timestamp: new Date(),
      originalError
    };

    this.logError(error);
    console.error('Translation network error:', originalError);
  }

  /**
   * Handle cache-related errors
   */
  static handleCacheError(operation: string, originalError: Error): void {
    const error: TranslationError = {
      errorType: TranslationErrorType.CACHE_ERROR,
      message: `Cache error during ${operation}: ${originalError.message}`,
      timestamp: new Date(),
      originalError
    };

    this.logError(error);
    console.warn('Translation cache error:', originalError);
  }

  /**
   * Handle unknown errors
   */
  static handleUnknownError(context: string, originalError: Error): void {
    const error: TranslationError = {
      errorType: TranslationErrorType.UNKNOWN_ERROR,
      message: `Unknown error in ${context}: ${originalError.message}`,
      timestamp: new Date(),
      originalError
    };

    this.logError(error);
    console.error('Unknown translation error:', originalError);
  }

  /**
   * Log error to internal error log
   */
  private static logError(error: TranslationError): void {
    // Add to error log
    this.errorLog.push(error);

    // Trim log if it exceeds max size
    if (this.errorLog.length > this.MAX_ERROR_LOG_SIZE) {
      this.errorLog = this.errorLog.slice(-this.MAX_ERROR_LOG_SIZE);
    }

    // Notify listeners
    this.errorListeners.forEach(listener => {
      try {
        listener(error);
      } catch (listenerError) {
        console.error('Error in translation error listener:', listenerError);
      }
    });
  }

  /**
   * Get recent translation errors
   */
  static getRecentErrors(count: number = 10): TranslationError[] {
    return this.errorLog.slice(-count);
  }

  /**
   * Get all translation errors
   */
  static getAllErrors(): TranslationError[] {
    return [...this.errorLog];
  }

  /**
   * Clear error log
   */
  static clearErrorLog(): void {
    this.errorLog = [];
  }

  /**
   * Get error statistics
   */
  static getErrorStats(): {
    totalErrors: number;
    errorsByType: Record<TranslationErrorType, number>;
    recentErrors: number;
  } {
    const errorsByType: Record<TranslationErrorType, number> = {
      [TranslationErrorType.MISSING_TRANSLATION]: 0,
      [TranslationErrorType.MISSING_TRANSLATION_FILE]: 0,
      [TranslationErrorType.INVALID_KEY]: 0,
      [TranslationErrorType.INVALID_LANGUAGE]: 0,
      [TranslationErrorType.AI_TRANSLATION_FAILED]: 0,
      [TranslationErrorType.NETWORK_ERROR]: 0,
      [TranslationErrorType.CACHE_ERROR]: 0,
      [TranslationErrorType.UNKNOWN_ERROR]: 0
    };

    this.errorLog.forEach(error => {
      errorsByType[error.errorType]++;
    });

    // Count errors in last 5 minutes
    const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
    const recentErrors = this.errorLog.filter(
      error => error.timestamp > fiveMinutesAgo
    ).length;

    return {
      totalErrors: this.errorLog.length,
      errorsByType,
      recentErrors
    };
  }

  /**
   * Add error listener for monitoring
   */
  static addErrorListener(listener: (error: TranslationError) => void): void {
    this.errorListeners.push(listener);
  }

  /**
   * Remove error listener
   */
  static removeErrorListener(listener: (error: TranslationError) => void): void {
    this.errorListeners = this.errorListeners.filter(l => l !== listener);
  }

  /**
   * Check if translation system is healthy
   * Returns false if there are too many recent errors
   */
  static isHealthy(): boolean {
    const stats = this.getErrorStats();
    
    // Consider unhealthy if more than 10 errors in last 5 minutes
    if (stats.recentErrors > 10) {
      return false;
    }

    // Consider unhealthy if more than 50% of recent errors are critical
    const criticalErrorTypes = [
      TranslationErrorType.MISSING_TRANSLATION_FILE,
      TranslationErrorType.NETWORK_ERROR
    ];

    const recentCriticalErrors = this.errorLog
      .slice(-20)
      .filter(error => criticalErrorTypes.includes(error.errorType))
      .length;

    if (recentCriticalErrors > 10) {
      return false;
    }

    return true;
  }

  /**
   * Get user-friendly error message
   */
  static getUserFriendlyMessage(errorType: TranslationErrorType): string {
    const messages: Record<TranslationErrorType, string> = {
      [TranslationErrorType.MISSING_TRANSLATION]: 'Some translations are not available. Showing English version.',
      [TranslationErrorType.MISSING_TRANSLATION_FILE]: 'Translation files could not be loaded. Using English.',
      [TranslationErrorType.INVALID_KEY]: 'Translation error occurred. Showing original text.',
      [TranslationErrorType.INVALID_LANGUAGE]: 'Language not supported. Using English.',
      [TranslationErrorType.AI_TRANSLATION_FAILED]: 'Translation unavailable. Showing original content.',
      [TranslationErrorType.NETWORK_ERROR]: 'Translation service temporarily unavailable. Please try again later.',
      [TranslationErrorType.CACHE_ERROR]: 'Translation cache error. Translations may be slower.',
      [TranslationErrorType.UNKNOWN_ERROR]: 'An error occurred. Please try again.'
    };

    return messages[errorType] || messages[TranslationErrorType.UNKNOWN_ERROR];
  }
}

export default TranslationErrorHandler;
